import { ActionCard } from "../../../../Review/ActionButtons/ActionCard";
import { useFrame } from "@/contexts/FrameContext";
import { useFlow } from "@/contexts/FlowContext";
import { useModel } from "@/contexts/ModelContext";
import { v4 as uuidv4 } from "uuid";
import { generateColorFromText } from "@/components/annotation/colors";
import type { Annotation } from "@/types/annotation";
import { useCallback } from "react";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent, AppSource } from "@/analytics/types";
import { useCurrentMedia } from "@/store";

export const PromptLabelNewObject = () => {
    const { promptLabels, annotationsForFile } = useModel();
    const { setSelectedAnnotationId } = useFrame();
    const { setCurrentFrameAnnotations, flowLocked } = useFlow();
    const { track } = useAnalytics();
    const currentMedia = useCurrentMedia();

    // Check if there's already an empty annotation (using draft property)
    const allAnnotations = Object.values(annotationsForFile).flat();
    const hasEmptyAnnotation = allAnnotations.some((anno) => anno.draft === true);

    const handleAddDraftAnnotation = useCallback(() => {
        // Generate a unique name for the new empty annotation
        const baseObjectName = "New Object";
        let newObjectName = baseObjectName;
        let counter = 1;

        // Find a unique name that doesn't already exist in prompt labels
        while (promptLabels.has(newObjectName)) {
            newObjectName = `${baseObjectName} ${counter}`;
            counter++;
        }

        // Track new object addition
        track({
            event: AnalyticsFlowEvent.NEW_OBJECT_ADDED,
            data: {
                appSource: AppSource.RAPID,
                objectName: newObjectName,
                creationMethod: "new_object_card",
                isDraft: true,
                existingObjectCount: promptLabels.size
            }
        });

        // Create a new empty annotation with fake placeholder values
        const newAnnotation: Annotation = {
            id: uuidv4(),
            empty: true, // Mark as empty/draft annotation
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            label: newObjectName,
            frameId: currentMedia?.frame?.id!,
            color: generateColorFromText(newObjectName),
            fileId: currentMedia?.file.id!
        };

        // Add the annotation to the current frame
        setCurrentFrameAnnotations((prev: Annotation[]) => [
            ...prev,
            { ...newAnnotation, modifiedAt: Date.now() }
        ]);

        // Select the newly created annotation
        setSelectedAnnotationId(newAnnotation.id);
    }, [
        currentMedia?.frame?.id,
        currentMedia?.file.id,
        promptLabels,
        setCurrentFrameAnnotations,
        setSelectedAnnotationId,
        track
    ]);

    // Don't show the "New Object" button when generating predictions
    if (flowLocked) {
        return null;
    }

    return (
        <ActionCard
            disabled={hasEmptyAnnotation}
            title="New Object Group"
            icon="fal fa-plus"
            type="secondary"
            size="sm"
            onClick={handleAddDraftAnnotation}
        />
    );
};
